import { ref, computed, getCurrentInstance, onUnmounted } from "vue";
import { apolloClient } from "@/plugins/apollo";
import FipaListener from "@/utils/listenerUtility";
/**
 * Ability to query in multiple chunks instead of one single large one that can cause heroku/server timeout.
 * @returns {Object} Function to start lazy loading and its related loading properties
 */
export function useLazyLoadingQuery() {
    const instance = getCurrentInstance();
    const currentTotalCount = ref(0);
    const totalCount = ref(undefined);
    const loading = ref(false);
    const loadingProgress = computed(() => {
        if (currentTotalCount.value == 0)
            return 0;
        return Math.floor((currentTotalCount.value / totalCount.value) * 100);
    });
    const intervalLoop = ref(null);
    let promiseResolve = null;
    /**
     * Ability to query in multiple chunks instead of one single large one that can cause heroku/server timeout.
     *
     * @param {gql} query GraphQl document
     * @param {Object} options Required options to make this functino work
     * @param {number} options.perQueryLimit Limit how many rows to load up per query
     * @param {number} options.totalCount Total number of how many rows to load from server.
     * @param {string} options.fieldName Property name where to get response row length from apollo result
     * @param {FetchPolicy} options.fetchPolicy Apollo fetchPolicy; Default network-only
     * @param {Object} options.variables Variables for apollo query. Offset and limit are automatically provided/overwritten in variables in this function
     */
    function initLazyLoadingQuery(query, options) {
        if (loading.value == true)
            return; // prevent creating new queries until current one gets completed
        clearLoadingState();
        let serverData = [];
        let offset = 0;
        currentTotalCount.value = 0;
        totalCount.value = options.totalCount;
        loading.value = true;
        const maxActiveQueriesCount = 6;
        let activeQueriesCount = 0;
        /**
         * When not giving totalCount, then when receiving a empty array results
         * assume there is nothing else to fetch more. Stop creating new queries and
         * wait for existing ones to complete before resolving promise.
         */
        let assumeChunkingCompleted = false;
        function resolve() {
            promiseResolve(serverData.filter(Boolean).flat());
            stopLazyLoadingQuery();
        }
        intervalLoop.value = setInterval(() => {
            var _a;
            if (options.totalCount && currentTotalCount.value >= options.totalCount) {
                resolve();
                return;
            }
            else if (assumeChunkingCompleted) {
                if (activeQueriesCount === 0) {
                    resolve();
                    return;
                }
                return;
            }
            if (activeQueriesCount == maxActiveQueriesCount) {
                return;
            }
            function totalCountOrAssumedCompleted() {
                return options.totalCount
                    ? offset < options.totalCount
                    : !assumeChunkingCompleted;
            }
            while (totalCountOrAssumedCompleted() &&
                activeQueriesCount < maxActiveQueriesCount) {
                activeQueriesCount += 1;
                apolloClient
                    .query({
                    query: typeof query === "function" ? query() : query,
                    variables: {
                        ...((_a = options.variables) !== null && _a !== void 0 ? _a : {}),
                        offset,
                        limit: options.perQueryLimit
                    },
                    fetchPolicy: options.fetchPolicy || "network-only"
                })
                    .then(({ data }) => {
                    var _a;
                    if (!options.totalCount) {
                        if (((_a = data[options.fieldName]) === null || _a === void 0 ? void 0 : _a.length) === 0) {
                            assumeChunkingCompleted = true;
                        }
                    }
                    else if (currentTotalCount.value + options.perQueryLimit <=
                        options.totalCount) {
                        currentTotalCount.value += options.perQueryLimit;
                    }
                    else {
                        currentTotalCount.value = options.totalCount;
                    }
                    if (options.fetchPolicy === "no-cache") {
                        serverData.push(data[options.fieldName]);
                    }
                    activeQueriesCount -= 1;
                })
                    .catch(error => {
                    stopLazyLoadingQuery();
                    if (options.fieldName) {
                        FipaListener.emit(`${options.fieldName}-chunkLoadingFailure`);
                    }
                    // @ts-ignore
                    instance.proxy.$root.$snackbarServerError(error, {
                        level: "error"
                    });
                });
                offset += options.perQueryLimit;
            }
        }, 200);
        return new Promise(resolve => {
            promiseResolve = resolve;
        });
    }
    // Albeit this only stops interval loop, it doesn't stop any active queries because apollo doesn't provide cancelling
    // specific queries
    function stopLazyLoadingQuery() {
        if (promiseResolve)
            promiseResolve([]);
        clearInterval(intervalLoop.value);
        intervalLoop.value = null;
        loading.value = false;
    }
    function clearLoadingState() {
        currentTotalCount.value = 0;
        totalCount.value = undefined;
    }
    onUnmounted(() => {
        stopLazyLoadingQuery();
    });
    return {
        startLazyLoading: initLazyLoadingQuery,
        stopLazyLoadingQuery,
        loading,
        loadingProgress,
        currentTotalCount,
        clearLoadingState
    };
}
